/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { useTranslation } from 'react-i18next';
import { ClbIcon } from '@celebration/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AuthContext from '../../../../app/contexts/auth';
import LanguageContext from '../../../../app/contexts/language';
import PreferencesConstants from '../../constants';
import Input from '../../../../common/components/input';
import IUserPreferences from '../../interfaces/IUserPreferences';
import LanguageSelector from '../language-selector/language-selector';
import CountrySelector from '../country-selector/country-selector';
import CountryConfirmationModal from '../country-confirmation-modal';
import { SULAHUB_ROLES } from '../../../../common/constants/roles';

function General() {
    const { t } = useTranslation();
    const { user, setUsername, isAuthorized } = AuthContext.useAuth();
    const { language, setLanguage } = LanguageContext.useLanguage();

    const [displayName, setDisplayName] = useState('');
    const [isEditing, setIsEditing] = useState<boolean[]>([
        false,
        false,
        false,
    ]);
    const [selectedPreferences, setSelectedPreferences] =
        useState<IUserPreferences>({
            country: user?.country!,
            language: language!,
        });
    const [isModalOpened, setIsModalOpened] = useState(false);

    useEffect(() => {
        const displayName = localStorage.getItem('DisplayName') ?? user!.name;
        setDisplayName(displayName);
    }, [user]);

    const generalOptions = useMemo(() => {
        return [
            {
                id: 0,
                key: t('ExhibitionName'),
                value: displayName,
                action: () => {
                    localStorage.setItem('DisplayName', displayName);
                    setUsername(displayName);
                },
            },
            {
                id: 1,
                key: t('Language'),
                value: PreferencesConstants.LANGUAGES[language!],
                action: () => {
                    setLanguage(selectedPreferences.language);
                },
            },
            {
                id: 2,
                key: t('OperationCountry'),
                value: user?.country,
                action: () => {
                    setIsModalOpened(true);
                },
            },
        ];
    }, [
        user,
        language,
        displayName,
        setUsername,
        setLanguage,
        selectedPreferences,
        t,
    ]);

    const closeModalCallback = useCallback(() => setIsModalOpened(false), []);

    return (
        <>
            <div className="preferences-inside-container">
                <div className="container-title">
                    <h1>{t('General')}</h1>
                </div>

                <div className="container-options">
                    {generalOptions.map(option => (
                        <div className="option" key={option.id}>
                            <div>
                                {option.id !== 2 && !isEditing[option.id] && (
                                    <>
                                        <span>{t(option.key)}</span>
                                        <span>{t(option.value!)}</span>
                                    </>
                                )}

                                {option.id === 2 &&
                                    isAuthorized([
                                        SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                                    ]) &&
                                    !isEditing[option.id] && (
                                        <>
                                            <span>{t(option.key)}</span>
                                            <span>{t(option.value!)}</span>
                                        </>
                                    )}

                                {option.id === 0 && isEditing[option.id] && (
                                    <Input
                                        label={t(option.key)}
                                        value={displayName}
                                        onChange={e =>
                                            setDisplayName(e.target.value)
                                        }
                                        dataTestId="exhibition-name-input"
                                    />
                                )}

                                {option.id === 1 && isEditing[option.id] && (
                                    <LanguageSelector
                                        selectedPreferences={{
                                            country: user?.country!,
                                            language: language!,
                                        }}
                                        setSelectedPreferences={
                                            setSelectedPreferences
                                        }
                                    />
                                )}

                                {option.id === 2 && isEditing[option.id] && (
                                    <CountrySelector
                                        selectedPreferences={{
                                            country: user?.country!,
                                            language: language!,
                                        }}
                                        setSelectedPreferences={
                                            setSelectedPreferences
                                        }
                                    />
                                )}
                            </div>

                            {(option.id !== 2 ||
                                isAuthorized([
                                    SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                                ])) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const editingField =
                                            isEditing[option.id];

                                        if (editingField) option.action();

                                        setIsEditing(s => {
                                            s[option.id] = !editingField;
                                            return [...s];
                                        });
                                    }}
                                    data-testid={`${option.id}-${isEditing[option.id] ? 'confirm' : 'edit'}-button`}
                                >
                                    <ClbIcon
                                        icon={
                                            isEditing[option.id]
                                                ? 'check'
                                                : 'edit'
                                        }
                                        size="md"
                                    />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <CountryConfirmationModal
                opened={isModalOpened}
                country={selectedPreferences.country}
                closeModalCallback={closeModalCallback}
            />
        </>
    );
}

export default General;
